import { inject, Injectable, NgZone } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';

export type KeyboardCallback = () => void;

/**
 * Thin abstraction over `@capacitor/keyboard` that isolates the plugin's
 * event names and lifecycle from consumers. Consumers register plain
 * callbacks and receive an unsubscribe function; the service owns the
 * underlying plugin listener registration and NgZone re-entry.
 *
 * On non-native platforms callbacks are never invoked and the returned
 * unsubscribe is a no-op.
 */
@Injectable({
    providedIn: 'root',
})
export class KeyboardService {
    private readonly ngZone = inject(NgZone);

    private willShowCallbacks = new Set<KeyboardCallback>();
    private willHideCallbacks = new Set<KeyboardCallback>();
    private hasPluginListeners = false;

    onKeyboardWillShow(callback: KeyboardCallback): () => void {
        return this.subscribe(this.willShowCallbacks, callback);
    }

    onKeyboardWillHide(callback: KeyboardCallback): () => void {
        return this.subscribe(this.willHideCallbacks, callback);
    }

    private subscribe(target: Set<KeyboardCallback>, callback: KeyboardCallback): () => void {
        if (!Capacitor.isNativePlatform()) {
            return () => undefined;
        }

        this.ensurePluginListeners();
        target.add(callback);

        return () => {
            target.delete(callback);
            this.teardownIfIdle();
        };
    }

    private ensurePluginListeners(): void {
        if (this.hasPluginListeners) return;
        this.hasPluginListeners = true;

        Keyboard.addListener('keyboardWillShow', () => {
            this.ngZone.run(() => this.willShowCallbacks.forEach((cb) => cb()));
        });
        Keyboard.addListener('keyboardWillHide', () => {
            this.ngZone.run(() => this.willHideCallbacks.forEach((cb) => cb()));
        });
    }

    private teardownIfIdle(): void {
        if (!this.hasPluginListeners) return;
        if (this.willShowCallbacks.size > 0 || this.willHideCallbacks.size > 0) return;

        this.hasPluginListeners = false;
        void Keyboard.removeAllListeners();
    }
}
